import { useQuery } from '@tanstack/react-query'
import {
  fetchRoomHistory, formatCount, formatDateTime, nameIn, translateError,
  type RoomHistoryEntry,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   **WHAT HAPPENED TO THIS ROOM.**

   Sprint 37 wrote this — the first reader `RoomStatusHistory` had had since Sprint 1
   created it — and wrote it inside `RoomBoard.tsx`, where it was the only thing that
   needed it. **Sprint 62 moved it here rather than copying it into W-21.** Two panels
   deciding for themselves what counts as a replayed action, and whether a history that
   failed to load may take down the form beside it, is this project's most repeated
   defect with the serial numbers filed off.

   **FETCHED WHEN SOMEBODY ASKS, NOT WITH THE BOARD.** The board is five hundred rooms
   refreshing every thirty seconds; pulling every room's history into it would be five
   hundred queries nobody reads. This is one query, for one room, when it is opened.
   ========================================================================= */

export function RoomHistory({
  roomId, t, locale,
}: {
  roomId: string
  t: (typeof STRINGS)['en']
  locale: 'en' | 'ar'
}) {
  const history = useQuery({
    queryKey: ['room-history', roomId],
    queryFn: ({ signal }) => fetchRoomHistory(roomId, { signal }),
    /* NO POLLING. A dialog is open for seconds, and a panel that rearranged itself while
       somebody was reading it would be answering a question they had already stopped
       asking. It is re-read the next time it is opened. */
    refetchOnWindowFocus: false,
  })

  if (history.isLoading) {
    return <span className="text-[12px] text-muted-fg">{t.histLoading}</span>
  }

  /* A FAILED HISTORY DOES NOT TAKE THE DIALOG WITH IT. The override beneath it still works,
     and a supervisor who came here to mark a room clean must not be stopped by a panel they
     were not looking at. The message is the product's own, translated.

     **`status` AND NOT `alert`.** An alert is assertive: it interrupts a screen reader
     mid-sentence, which is right for a refusal of something somebody just pressed and wrong
     for a subordinate panel that could not load beside a form that works. It also kept the
     dialog's real refusal from being the only alert on the screen, which is how a test
     found it. */
  if (history.error) {
    return (
      <div role="status" className="rounded-hk border border-border bg-muted/40 px-3 py-2 text-[12px] text-muted-fg">
        {String(translateError(history.error, locale).message)}
      </div>
    )
  }

  const view = history.data!

  if (view.entries.length === 0) {
    return (
      <span className="text-[12px] text-muted-fg">
        {fill(t.histEmpty, { days: formatCount(view.days, locale) })}
      </span>
    )
  }

  return (
    <div className="flex flex-col gap-1.5">
      <h4 className="text-[11.5px] font-semibold uppercase tracking-[0.04em] text-muted-fg">
        {t.histTitle}
      </h4>

      {/* CAPPED AND SCROLLED. Fifty entries is taller than the dialog, and a panel that
          grows past the bottom of the window takes the buttons with it. */}
      <ol className="flex max-h-64 flex-col gap-1.5 overflow-y-auto pe-1">
        {view.entries.map((e) => (
          <HistoryRow key={e.historyId} entry={e} t={t} locale={locale} />
        ))}
      </ol>

      {view.truncated && (
        <span className="text-[11px] text-muted-fg">
          {fill(t.histTruncated, { count: formatCount(view.entries.length, locale) })}
        </span>
      )}
    </div>
  )
}

function HistoryRow({
  entry, t, locale,
}: {
  entry: RoomHistoryEntry
  t: (typeof STRINGS)['en']
  locale: 'en' | 'ar'
}) {
  const captured = new Date(entry.capturedAtUtc)
  const recorded = new Date(entry.atUtc)

  /* A MINUTE OF SLACK. The two timestamps are written by the same code path on an online
     action and can differ by milliseconds; saying "done at 09:14, recorded at 09:14" on
     every row would be noise on every row, and would bury the case it exists for. */
  const replayed = recorded.getTime() - captured.getTime() > 60_000

  const who = entry.actorName
    ? fill(t.histBy, { name: nameIn(locale, entry.actorName, entry.actorNameAr) })
    : t.histByNobody

  return (
    <li className="rounded-hk border border-border bg-card px-2.5 py-1.5 text-[12px]">
      <div className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="font-medium">{t.roomEvents[entry.event]}</span>
        <span className="text-muted-fg"><bdi>{who}</bdi></span>
        <span className="ms-auto tnum text-[11px] text-muted-fg">
          {formatDateTime(captured, locale)}
        </span>
      </div>

      {replayed && (
        <div className="tnum text-[11px] text-muted-fg">
          {fill(t.histCapturedEarlier, {
            time: formatDateTime(captured, locale),
            recorded: formatDateTime(recorded, locale),
          })}
        </div>
      )}

      {/* THE REASON IS THE POINT OF THE ROW IT IS ON. An override or a cancelled clean
          without its reason is the record saying somebody did something and not why. */}
      {entry.reason && (
        <div className="mt-0.5 text-[11.5px] text-muted-fg" dir="auto">“{entry.reason}”</div>
      )}
    </li>
  )
}
